/**
 * One range of a ranking formula, IN the Flow Optimizer's own formula group
 * card (user, 12/10/2026: "use the same component as flow optimiser"): its
 * card shell, its header row (name, date range — whole days only here —,
 * Sliders | Blocks, remove) and its body (sliders, or blocks with
 * sub-formulas). No layers here; the header's right side carries the range's
 * share when several exist and the "first range" mark ("Not enough data" and
 * every "the formula's first range" rule read it).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  DROP_GEN_HOUR_DATE_RANGES,
  type DropGenHourFormulaGroup,
  type FormulaGroup,
  type FormulaGroupMode,
} from '@trafficpulse/shared';

import { Badge, NumberField } from '@/components/ui';
import { Card, CardContent } from '@/components/ui/layout/Card';
import { Button } from '@/components/ui/layout/Button';
import { recomputeGroupExpression, switchModeBody } from '@/features/flow-optimizer/tabs/calculation/formula-group.utils';
import FormulaGroupCardBody from '@/features/flow-optimizer/tabs/calculation/FormulaGroupCardBody';
import { astToSliders, GroupHeaderRow, slidersToAst } from '@/features/flow-optimizer/tabs/calculation/group';


interface Props {
  group: DropGenHourFormulaGroup;
  index: number;
  count: number;
  /** This range's share of the score, 0..100 (shown with several ranges). */
  share: number;
  onChange: (next: DropGenHourFormulaGroup) => void;
  onShare: (share: number) => void;
  onRemove: () => void;
  /** Route tests: the range stays today and the picker does not open. */
  dateLocked?: boolean;
}

const Trailing: FC<Pick<Props, 'index' | 'count' | 'share' | 'onShare'>> = ({ index, count, share, onShare }) => (
  <span className="flex items-center gap-1.5">
    {index === 0 ? <Badge tone="info" title="“Not enough data” and every rule over “the formula's first range” read this range">first range</Badge> : null}
    {count > 1 ? (
      <label className="flex items-center gap-1 text-xs text-muted-foreground">
        share
        <NumberField value={Math.round(share)} onChange={onShare} min={0} max={100} aria-label={`Range ${index + 1} share`} className="h-7 w-16 rounded-md border border-input bg-transparent px-2 text-right font-mono text-xs" />%
      </label>
    ) : null}
  </span>
);

export const HourFormulaGroupCard: FC<Props> = ({ group, index, count, share, onChange, onShare, onRemove, dateLocked = false }) => {
  const [open, setOpen] = useState(true);
  const update = (patch: Partial<DropGenHourFormulaGroup>) => {
    const next = { ...group, ...patch };
    onChange({ ...next, expression: recomputeGroupExpression(next as FormulaGroup) });
  };
  const switchMode = (mode: FormulaGroupMode) => update(switchModeBody(group as FormulaGroup, mode, slidersToAst, astToSliders) as Partial<DropGenHourFormulaGroup>);
  const bodyId = `ranking-range-${group.id}`;
  return (
    <Card className="overflow-hidden" data-testid="ranking-range">
      <CardContent className="space-y-3 p-3">
        <div className="flex items-start gap-2">
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-expanded={open} aria-controls={bodyId} title={open ? 'Collapse formula' : 'Expand formula'} onClick={() => setOpen((v) => !v)}>
            {open ? <ChevronDown className="h-3 w-3" aria-hidden /> : <ChevronRight className="h-3 w-3" aria-hidden />}
          </Button>
          <div className="min-w-0 flex-1">
            <GroupHeaderRow
              name={group.name}
              namePlaceholder={`Range ${index + 1}`}
              dateRange={group.dateRange}
              dateRangeCustomFrom={group.dateRangeCustomFrom}
              dateRangeCustomTo={group.dateRangeCustomTo}
              datePresets={DROP_GEN_HOUR_DATE_RANGES}
              dateLocked={dateLocked}
              mode={group.mode}
              onNameChange={(name) => update({ name })}
              onDateRangeCommit={(patch) => update(patch)}
              onModeChange={switchMode}
              onRemove={count > 1 ? onRemove : undefined}
              trailingActions={<Trailing index={index} count={count} share={share} onShare={onShare} />}
            />
          </div>
        </div>
        <FormulaGroupCardBody
          id={bodyId}
          expanded={open}
          group={group as FormulaGroup}
          expression={group.expression}
          onSlidersChange={(sliders) => update({ sliders })}
          onBlocksChange={(blocks) => update({ blocks })}
        />
      </CardContent>
    </Card>
  );
};
